import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import styles from './AddVolume.module.css';
import { isbnApi, searchApi, libraryApi, getMediaUrl } from '../api';
import { BrowserMultiFormatReader } from '@zxing/library';

function AddVolume({ userId }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('single');
  const [inputMethod, setInputMethod] = useState('manual');
  const [isbn, setIsbn] = useState('');
  const [isbnList, setIsbnList] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [history, setHistory] = useState([]);
  // Fortschritt für Bulk-Textfeld-Import (§: Ladebalken inkl. Fehlerzahl, damit
  // klar ist, wann der Import fertig ist und man die Seite gefahrlos verlassen
  // kann) - bleibt nach Abschluss stehen (kein Auto-Reset), bis ein neuer
  // Bulk-Import gestartet wird.
  const [bulkProgress, setBulkProgress] = useState(null);
  // Serie per Titelsuche (§9: Serie ohne ISBN finden und hinzufügen)
  const [seriesSearchTerm, setSeriesSearchTerm] = useState('');
  const [seriesSearchResults, setSeriesSearchResults] = useState([]);
  const [isSeriesSearching, setIsSeriesSearching] = useState(false);
  const [addingSeriesId, setAddingSeriesId] = useState(null);
  const [seriesMessage, setSeriesMessage] = useState(null);
  // Physischer Scanner
  const [scannerReady, setScannerReady] = useState(false);
  const scanInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanLoopRef = useRef(null);
  const codeReaderRef = useRef(null); // ZXing-Fallback für Firefox/Safari
  const scanBufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  // Aktuelle mode/inputMethod-Werte für Closures in onBarcodeDetected (useCallback mit [] deps)
  const modeRef = useRef(mode);
  const inputMethodRef = useRef(inputMethod);
  const lastScannedRef = useRef({ code: null, time: 0 });

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { inputMethodRef.current = inputMethod; }, [inputMethod]);

  useEffect(() => {
    // Load history from localStorage
    const savedHistory = localStorage.getItem('addVolumeHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Funktionales Update, damit aufeinanderfolgende Aufrufe (Bulk-Textfeld-Schleife,
  // Dauerscan im Kamera-Modus) nicht denselben veralteten history-Stand überschreiben,
  // sondern sich jeweils auf den zuletzt gesetzten Stand aufbauen.
  //
  // Fehlgeschlagene Einträge bleiben IMMER erhalten (auch bei großen Bulk-Importen
  // mit hunderten Bänden) - nur erfolgreiche Einträge werden auf die letzten 20
  // begrenzt, damit man nach einem großen Import alle Fehler für den Support
  // nachvollziehen kann, statt dass sie von neueren Erfolgen verdrängt werden.
  const saveToHistory = (item) => {
    setHistory(prev => {
      const combined = [item, ...prev];
      let successCount = 0;
      const newHistory = combined.filter(h => {
        if (h.success) {
          successCount += 1;
          return successCount <= 20;
        }
        return true;
      });
      localStorage.setItem('addVolumeHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  /**
   * Globale Manga-Suche für "Serie per Titelsuche" (§9)
   */
  const handleSeriesSearch = async (e) => {
    e.preventDefault();
    if (!seriesSearchTerm.trim()) return;

    setIsSeriesSearching(true);
    setSeriesMessage(null);
    try {
      const result = await searchApi.searchMangas(seriesSearchTerm.trim(), { limit: 30 });
      if (result.success) {
        const items = result.data?.items || [];
        setSeriesSearchResults(items);
        if (items.length === 0) {
          setSeriesMessage({ type: 'info', text: t('addVolume.seriesNoResults') });
        }
      } else {
        setSeriesMessage({ type: 'error', text: result.error?.message || t('addVolume.seriesSearchFailed') });
        setSeriesSearchResults([]);
      }
    } catch (err) {
      setSeriesMessage({ type: 'error', text: t('addVolume.seriesNetworkError') });
    } finally {
      setIsSeriesSearching(false);
    }
  };

  /**
   * Serie ohne ISBN zur Sammlung hinzufügen (§9) - Backend legt automatisch
   * alle bekannten Bände der Serie an.
   */
  const handleAddSeries = async (manga) => {
    const mangaId = manga.manga_id;
    setAddingSeriesId(mangaId);
    setSeriesMessage(null);
    try {
      const result = await libraryApi.addSeries(mangaId);
      if (result.success) {
        setSeriesMessage({ type: 'success', text: t('addVolume.seriesAdded', { title: manga.title }) });
        setSeriesSearchResults((prev) => prev.filter((m) => m.manga_id !== mangaId));
      } else {
        const code = result.error?.code;
        if (code === 'ALREADY_OWNED' || code === 'CONFLICT') {
          setSeriesMessage({ type: 'info', text: t('addVolume.seriesAlreadyOwned', { title: manga.title }) });
        } else {
          setSeriesMessage({ type: 'error', text: result.error?.message || t('addVolume.seriesAddFailed') });
        }
      }
    } catch (err) {
      setSeriesMessage({ type: 'error', text: t('addVolume.seriesAddNetworkError') });
    } finally {
      setAddingSeriesId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      if (mode === 'single') {
        await addSingleVolume(isbn.trim());
      } else {
        const isbns = isbnList
          .split(/[\n,]/)
          .map(i => i.trim())
          .filter(i => i.length > 0);

        // Import läuft sequentiell (eine ISBN nach der anderen, nicht als ein
        // großer Batch-Request) - jede fehlschlagende ISBN durchläuft dabei die
        // komplette externe Provider-Kette, was mehrere Sekunden dauern kann.
        setBulkProgress({ total: isbns.length, processed: 0, errors: 0 });

        for (let i = 0; i < isbns.length; i++) {
          const result = await addSingleVolume(isbns[i]);
          setBulkProgress(prev => prev ? {
            ...prev,
            processed: prev.processed + 1,
            errors: prev.errors + (result?.success ? 0 : 1)
          } : prev);
          if (result?.error?.code === 'UNAUTHORIZED') {
            // Sitzung während eines langen Bulk-Imports abgelaufen - sofort abbrechen
            // statt die restlichen ISBNs sinnlos durchzulaufen (würden alle fehlschlagen)
            setMessage({ type: 'error', text: t('addVolume.sessionExpiredDuringBulk', { processed: i + 1, total: isbns.length }) });
            navigate('/login');
            break;
          }
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('addVolume.errorGeneric') });
    } finally {
      setIsLoading(false);
    }
  };

  const addSingleVolume = async (isbnValue) => {
    try {

      const result = await isbnApi.addToLibrary(isbnValue);


      const historyItem = {
        isbn: isbnValue,
        title: result.data?.band_title || t('addVolume.unknownTitle'),
        success: result.success,
        message: result.success ? t('addVolume.successAdded') : (result.error?.message || t('addVolume.errorDefault')),
        timestamp: new Date().toISOString()
      };

      saveToHistory(historyItem);

      if (result.success) {
        setMessage({ type: 'success', text: t('addVolume.successAddedWithTitle', { title: result.data?.band_title || isbnValue }) });
        setIsbn('');
        setIsbnList('');
      } else {
        setMessage({ type: 'warning', text: result.error?.message || t('addVolume.warningAddFailed') });
      }
      return result;
    } catch (error) {
      console.error('❌ Fehler beim Hinzufügen:', error);

      const historyItem = {
        isbn: isbnValue,
        title: t('addVolume.errorDefault'),
        success: false,
        message: error.message,
        timestamp: new Date().toISOString()
      };

      saveToHistory(historyItem);
      setMessage({ type: 'error', text: t('addVolume.errorWithMessage', { message: error.message }) });
      return { success: false, error: { code: 'NETWORK_ERROR', message: error.message } };
    }
  };

  /**
   * Barcode erkannt. Im Bulk-Modus mit aktiver Kamera wird NICHT gestoppt:
   * jeder erkannte Code wird direkt hinzugefügt, im Verlauf rechts angezeigt,
   * und der Scan läuft weiter (Dauerscan für mehrere Bände hintereinander).
   * Rückgabewert steuert, ob der Scan-Loop (Pfad A) weiterlaufen soll.
   */
  const onBarcodeDetected = useCallback((code) => {
    if (modeRef.current === 'bulk' && inputMethodRef.current === 'camera') {
      const now = Date.now();
      // Denselben, noch im Bild sichtbaren Barcode nicht mehrfach hintereinander hinzufügen
      if (lastScannedRef.current.code === code && now - lastScannedRef.current.time < 3000) {
        return true;
      }
      lastScannedRef.current = { code, time: now };
      setMessage({ type: 'info', text: t('addVolume.scannerScanned', { code }) });
      isbnApi.addToLibrary(code).then(result => {
        const item = {
          isbn: code,
          title: result.data?.band_title || code,
          success: result.success,
          message: result.success ? t('addVolume.successAdded') : (result.error?.message || t('addVolume.errorDefault')),
          timestamp: new Date().toISOString()
        };
        saveToHistory(item);
        if (result.success) {
          setMessage({ type: 'success', text: t('addVolume.scannerAdded', { title: result.data?.band_title || code }) });
        } else if (result.error?.code === 'UNAUTHORIZED') {
          // Sitzung während eines langen Dauerscans abgelaufen - Scan stoppen statt
          // stumm weiterzuscannen (jeder weitere Scan würde ohnehin fehlschlagen)
          setMessage({ type: 'error', text: t('addVolume.sessionExpiredDuringScan') });
          stopCamera();
          navigate('/login');
        } else {
          setMessage({ type: 'warning', text: result.error?.message || t('addVolume.warningAddFailed') });
        }
      });
      return true;
    }

    stopCamera();
    setIsbn(code);
    setInputMethod('manual');
    setMessage({ type: 'success', text: t('addVolume.barcodeDetected', { code }) });
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Startet nur den "Kamera aktiv"-Zustand - das <video>-Element wird dadurch
  // gemountet. Das eigentliche Anhängen des Streams passiert im useEffect
  // unten, ERST NACHDEM das <video>-Tag im DOM existiert (siehe dortiger
  // Kommentar für den Grund).
  const startCamera = () => {
    lastScannedRef.current = { code: null, time: 0 };
    setMessage({ type: 'info', text: t('addVolume.cameraStarting') });
    setIsCameraActive(true);
  };

  // Läuft erst, nachdem React durch isCameraActive=true das <video>-Element
  // tatsächlich gerendert hat - vorher war videoRef.current beim Start noch
  // null (React committet den Render erst nach dem setIsCameraActive-Aufruf),
  // wodurch der Stream nie am <video>-Tag ankam ("nur schwarzes Bild").
  useEffect(() => {
    if (!isCameraActive) return;

    let cancelled = false;

    const attachCamera = async () => {
      try {
        // Pfad A: Native BarcodeDetector (Chrome 83+, Edge 83+, Android Chrome)
        if ('BarcodeDetector' in window) {
          const detector = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'qr_code', 'upc_a', 'upc_e']
          });
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
          });
          if (cancelled) { mediaStream.getTracks().forEach(t => t.stop()); return; }

          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            // Manche Browser starten die Wiedergabe trotz autoPlay-Attribut nicht
            // zuverlässig von selbst (Autoplay-Policy) - explizit anstoßen.
            videoRef.current.play().catch((playErr) => {
              console.warn('Video play() fehlgeschlagen:', playErr);
            });
          }
          setMessage({ type: 'info', text: t('addVolume.cameraActive') });

          const scan = async () => {
            if (cancelled) return;
            if (!videoRef.current || videoRef.current.readyState < 2) {
              scanLoopRef.current = requestAnimationFrame(scan);
              return;
            }
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes.length > 0) {
                const shouldContinue = onBarcodeDetected(codes[0].rawValue);
                if (!shouldContinue) return;
              }
            } catch (_) { /* Frame noch nicht bereit */ }
            scanLoopRef.current = requestAnimationFrame(scan);
          };
          scanLoopRef.current = requestAnimationFrame(scan);
          return;
        }

        // Pfad B: ZXing-Fallback (Firefox, Safari, alle anderen Browser)
        // decodeFromConstraints verwaltet den Stream selbst - videoRef.current
        // ist hier (anders als vorher) garantiert bereits gesetzt.
        const reader = new BrowserMultiFormatReader();
        codeReaderRef.current = reader;

        setMessage({ type: 'info', text: 'Kamera aktiv – Barcode in den Rahmen halten' });

        await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
          videoRef.current,
          (result) => {
            if (result && !cancelled) onBarcodeDetected(result.getText());
          }
        );
      } catch (error) {
        if (cancelled) return;
        console.error('Kamera-Fehler:', error);
        const msg = error.name === 'NotAllowedError'
          ? t('addVolume.cameraDenied')
          : t('addVolume.cameraError', { message: error.message });
        setMessage({ type: 'error', text: msg });
        setIsCameraActive(false);
      }
    };

    attachCamera();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraActive]);

  const stopCamera = () => {
    // Native BarcodeDetector-Loop stoppen
    if (scanLoopRef.current) {
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    // ZXing-Reader stoppen (reset() stoppt Stream + Decode)
    if (codeReaderRef.current) {
      try { codeReaderRef.current.reset(); } catch (_) {}
      codeReaderRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setIsCameraActive(false);
    if (videoRef.current) videoRef.current.srcObject = null;
    // Kamera-Status-Message entfernen (Barcode-Erkennungs-Messages bleiben)
    setMessage(prev =>
      prev?.text === t('addVolume.cameraActive') ? null : prev
    );
  };

  // Physischer Scanner: empfängt Tastatur-Input mit Scanner-Geschwindigkeit
  const handleScannerKeyDown = useCallback((e) => {
    const now = Date.now();
    const delta = now - lastKeyTimeRef.current;
    lastKeyTimeRef.current = now;

    if (e.key === 'Enter') {
      e.preventDefault();
      const code = scanBufferRef.current.trim();
      scanBufferRef.current = '';
      if (code.length >= 8) {
        setIsbn(code);
        setMessage({ type: 'success', text: t('addVolume.scannerScanned', { code }) });
        // Direkt einreichen
        isbnApi.addToLibrary(code).then(result => {
          const item = {
            isbn: code,
            title: result.data?.band_title || code,
            success: result.success,
            message: result.success ? t('addVolume.scannerSuccess') : (result.error?.message || t('addVolume.errorDefault')),
            timestamp: new Date().toISOString()
          };
          saveToHistory(item);
          if (result.success) {
            setMessage({ type: 'success', text: t('addVolume.scannerAdded', { title: result.data?.band_title || code }) });
          } else {
            setMessage({ type: 'warning', text: result.error?.message || t('addVolume.seriesAddFailed') });
          }
        });
      }
      return;
    }

    if (e.key.length === 1) {
      // Scanner-Zeichen kommen sehr schnell (< 50ms); Tastatur langsamer
      if (delta > 500 && scanBufferRef.current.length === 0) {
        // Erster Buchstabe nach langer Pause: neuer Scan
      }
      scanBufferRef.current += e.key;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fokus auf Scanner-Input halten
  useEffect(() => {
    if (inputMethod === 'scanner' && scannerReady && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [inputMethod, scannerReady]);

  // startBarcodeDetection wird nicht mehr verwendet (ZXing/BarcodeDetector übernehmen das)

  useEffect(() => {
    // Cleanup camera when input method changes or component unmounts
    if (inputMethod !== 'camera' && stream) {
      stopCamera();
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMethod]);
  
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return t('addVolume.justNow');
    if (diff < 3600000) return t('addVolume.minutesAgo', { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return t('addVolume.hoursAgo', { count: Math.floor(diff / 3600000) });

    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('addVolume.title')}</h1>
        <p>{t('addVolume.subtitle')}</p>
        <div className={styles.headerHint}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          <span><Trans i18nKey="addVolume.headerHint" components={[<strong key="0" />, <strong key="1" />]} /></span>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.addSection}>
          {/* Mode Selector (nicht relevant für Serie per Titelsuche) */}
          {inputMethod !== 'series' && (
            <div className={styles.modeSelector}>
              <button
                className={`${styles.modeButton} ${mode === 'single' ? styles.active : ''}`}
                onClick={() => setMode('single')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="2" strokeLinecap="round" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {t('addVolume.modeSingle')}
              </button>
              <button
                className={`${styles.modeButton} ${mode === 'bulk' ? styles.active : ''}`}
                onClick={() => setMode('bulk')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2" />
                  <path d="M7 8h10M7 12h10M7 16h7" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {t('addVolume.modeBulk')}
              </button>
            </div>
          )}

          {/* Input Method Selector */}
          <div className={styles.inputMethodSelector}>
            <button
              className={`${styles.methodButton} ${inputMethod === 'manual' ? styles.active : ''}`}
              onClick={() => {
                setInputMethod('manual');
                stopCamera();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="2" strokeLinecap="round" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('addVolume.methodManual')}
            </button>
            <button
              className={`${styles.methodButton} ${inputMethod === 'camera' ? styles.active : ''}`}
              onClick={() => {
                setInputMethod('camera');
                startCamera();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="13" r="4" strokeWidth="2" />
              </svg>
              {t('addVolume.methodCamera')}
            </button>
            <button
              className={`${styles.methodButton} ${inputMethod === 'scanner' ? styles.active : ''}`}
              onClick={() => {
                setInputMethod('scanner');
                stopCamera();
                setScannerReady(true);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                <line x1="7" y1="12" x2="17" y2="12"/>
              </svg>
              {t('addVolume.methodScanner')}
            </button>
            <button
              className={`${styles.methodButton} ${inputMethod === 'series' ? styles.active : ''}`}
              onClick={() => {
                setInputMethod('series');
                stopCamera();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="10" cy="10" r="7" strokeWidth="2" />
                <path d="M21 21l-6-6" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {t('addVolume.methodSeries')}
            </button>
          </div>

          {/* Serie per Titelsuche: eigener Ablauf, kein ISBN-Formular */}
          {inputMethod === 'series' ? (
            <div className={styles.seriesSearchSection}>
              <p className={styles.seriesSearchHint}>
                {t('addVolume.seriesHint')}
              </p>
              <form onSubmit={handleSeriesSearch} className={styles.seriesSearchForm}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={t('addVolume.seriesSearchPlaceholder')}
                  value={seriesSearchTerm}
                  onChange={(e) => setSeriesSearchTerm(e.target.value)}
                  autoFocus
                />
                <button type="submit" className={styles.submitButton} disabled={isSeriesSearching || !seriesSearchTerm.trim()}>
                  {isSeriesSearching ? t('addVolume.searching') : t('addVolume.search')}
                </button>
              </form>

              {seriesMessage && (
                <div className={`${styles.message} ${styles[seriesMessage.type]}`}>
                  {seriesMessage.text}
                </div>
              )}

              <div className={styles.seriesSearchResults}>
                {seriesSearchResults.map((manga) => (
                  <div key={manga.manga_id} className={styles.seriesSearchResultRow}>
                    {manga.cover_media_key ? (
                      <img src={getMediaUrl(manga.cover_media_key)} alt={manga.title} className={styles.seriesSearchResultCover} />
                    ) : (
                      <div className={styles.seriesSearchResultCoverPlaceholder} />
                    )}
                    <div className={styles.seriesSearchResultInfo}>
                      <span className={styles.seriesSearchResultTitle}>{manga.title}</span>
                      {manga.publisher && <span className={styles.seriesSearchResultPublisher}>{manga.publisher}</span>}
                    </div>
                    <button
                      type="button"
                      className={styles.seriesSearchResultButton}
                      disabled={addingSeriesId === manga.manga_id}
                      onClick={() => handleAddSeries(manga)}
                    >
                      {addingSeriesId === manga.manga_id ? '...' : t('addVolume.addButton')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {inputMethod === 'manual' ? (
              mode === 'single' ? (
                <div className={styles.formGroup}>
                  <label htmlFor="isbn">{t('addVolume.isbnLabel')}</label>
                  <input
                    id="isbn"
                    type="text"
                    className={styles.input}
                    placeholder={t('addVolume.isbnPlaceholder')}
                    value={isbn}
                    onChange={(e) => setIsbn(e.target.value)}
                    required
                  />
                </div>
              ) : (
                <div className={styles.formGroup}>
                  <label htmlFor="isbnList">{t('addVolume.isbnListLabel')}</label>
                  <textarea
                    id="isbnList"
                    className={styles.textarea}
                    placeholder="9783551773487&#10;9783551773494&#10;9783551773500"
                    value={isbnList}
                    onChange={(e) => setIsbnList(e.target.value)}
                    rows={6}
                    required
                  />
                </div>
              )
            ) : inputMethod === 'scanner' ? (
              <div className={styles.scannerContainer}>
                <div className={styles.scannerStatus}>
                  <div className={styles.scannerPulse}></div>
                  <span>{t('addVolume.scannerReady')}</span>
                </div>
                <p className={styles.scannerHint}>
                  {t('addVolume.scannerHint')}
                </p>
                <input
                  ref={scanInputRef}
                  className={styles.scannerHiddenInput}
                  type="text"
                  value=""
                  onChange={() => {}}
                  onKeyDown={handleScannerKeyDown}
                  onBlur={() => { setTimeout(() => scanInputRef.current?.focus(), 50); }}
                  autoFocus
                  aria-label={t('addVolume.scannerInputLabel')}
                />
                <button
                  type="button"
                  className={styles.focusBtn}
                  onClick={() => scanInputRef.current?.focus()}
                >
                  ↖ {t('addVolume.scannerFocusBtn')}
                </button>
              </div>
            ) : (
              <div className={styles.cameraContainer}>
                {mode === 'bulk' && (
                  <p className={styles.bulkCameraHint}>{t('addVolume.bulkCameraHint')}</p>
                )}
                {!isCameraActive ? (
                  <div className={styles.cameraPlaceholder}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="7" width="18" height="14" rx="2" strokeWidth="2" />
                      <path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" strokeWidth="2" />
                      <path d="M8 12h8" strokeWidth="2" strokeLinecap="round" />
                      <path d="M8 15h5" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <p>{t('addVolume.cameraTitle')}</p>
                    <button
                      type="button"
                      className={styles.cameraButton}
                      onClick={startCamera}
                    >
                      {t('addVolume.cameraStart')}
                    </button>
                  </div>
                ) : (
                  <div className={styles.cameraOverlay}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={styles.cameraVideo}
                    />
                    <div className={styles.scanLine}></div>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    <div className={styles.cameraControls}>
                      <button
                        type="button"
                        className={styles.stopCameraButton}
                        onClick={stopCamera}
                      >
                        {t('addVolume.cameraStop')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {bulkProgress && mode === 'bulk' && inputMethod === 'manual' && (
              <div className={`${styles.bulkProgress} ${bulkProgress.processed >= bulkProgress.total ? styles.done : ''}`}>
                <div className={styles.bulkProgressBarTrack}>
                  <div
                    className={styles.bulkProgressBarFill}
                    style={{ width: `${bulkProgress.total > 0 ? Math.round((bulkProgress.processed / bulkProgress.total) * 100) : 0}%` }}
                  />
                </div>
                <div className={styles.bulkProgressText}>
                  <span>
                    {bulkProgress.processed >= bulkProgress.total
                      ? t('addVolume.bulkProgressDone', { total: bulkProgress.total })
                      : t('addVolume.bulkProgressRunning', { processed: bulkProgress.processed, total: bulkProgress.total })}
                  </span>
                  {bulkProgress.errors > 0 && (
                    <span className={styles.bulkProgressErrors}>
                      {t('addVolume.bulkProgressErrors', { count: bulkProgress.errors })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {message && (
              <div className={`${styles.message} ${styles[message.type]}`}>
                {message.text}
              </div>
            )}

            {!(mode === 'bulk' && inputMethod === 'camera') && (
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading || (inputMethod === 'camera' && !isbn)}
              >
                {isLoading ? t('addVolume.submitLoading') : t('addVolume.submitAdd')}
              </button>
            )}
          </form>
          )}
        </div>

        {/* History Section */}
        <div className={styles.historySection}>
          <div className={styles.historyHeader}>
            <h2>{t('addVolume.historyTitle')}</h2>
            {history.length > 0 && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  setHistory([]);
                  localStorage.removeItem('addVolumeHistory');
                }}
              >
                {t('addVolume.historyClear')}
              </button>
            )}
          </div>

          <div className={styles.historyList}>
            {history.length === 0 ? (
              <div className={styles.emptyHistory}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p>{t('addVolume.historyEmpty')}</p>
              </div>
            ) : (
              history.map((item, index) => (
                <div 
                  key={index} 
                  className={`${styles.historyItem} ${item.success ? styles.success : styles.failed}`}
                >
                  <div className={styles.historyIcon}>
                    {item.success ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                        <path d="M17 6l-10 10-4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                        <circle cx="10" cy="10" r="8" strokeWidth="2"/>
                        <path d="M10 6v4" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="10" cy="14" r="0.5" fill="currentColor"/>
                      </svg>
                    )}
                  </div>
                  <div className={styles.historyContent}>
                    <div className={styles.historyTitle}>{item.title}</div>
                    <div className={styles.historyMeta}>
                      <span className={styles.historyIsbn}>{t('addVolume.historyIsbnLabel', { isbn: item.isbn })}</span>
                      <span className={styles.historyTime}>{formatDate(item.timestamp)}</span>
                    </div>
                    {!item.success && (
                      <div className={styles.historyError}>{item.message}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddVolume;
